import React, { FC } from 'react';
import styled from 'styled-components';
import * as tokens from '@utmost.co/utmost-design-tokens';
import appTheme from '../../theme/appTheme';
import { Uds } from '../../types';
import type { GoogleIconTypes } from './GoogleIconTypes';
import { EmbeddedSvgNames, allEmbeddedSvgs } from './EmbeddedIcons';

export type IconName = SupportedIconNames;
type IconSize = 'small' | 'medium' | 'large';

export type SupportedIconNames = GoogleIconTypes | EmbeddedSvgNames;

export type SystemIconProps = Uds<{
  name: SupportedIconNames;
  color?: string;
  size?: number | 'small' | 'medium' | 'large';
  className?: string;
  style?: React.CSSProperties;
}>;

const iconSizes: { [prop in IconSize]: number } = {
  small: appTheme.iconSize.small,
  medium: appTheme.iconSize.medium,
  large: appTheme.iconSize.large
};

export const SystemIcon: FC<SystemIconProps & Omit<React.HTMLProps<HTMLSpanElement>, 'size'>> = ({
  name,
  className,
  color = tokens.colorOnSurfaceVariant,
  size = 'medium',
  dataTest,
  style,
  ...spanProps
}) => {
  const iconColor = color || tokens.colorOnSurfaceVariant;
  const iconSize = typeof size === 'number' ? size : iconSizes[size];
  const fillColor = !iconColor || iconColor === 'inherit' ? 'currentColor' : iconColor;

  let content: string | JSX.Element;

  // If its an embedded svg icon, render it
  if (name.endsWith('_custom')) {
    content = allEmbeddedSvgs[name as keyof typeof allEmbeddedSvgs];
  } else content = name.toLowerCase();

  return (
    <StyledIconWrapper
      style={{ fontSize: iconSize, color: iconColor, maxWidth: iconSize, fill: iconColor, ...style }}
      data-test={dataTest}
      data-icon-name={name}
      className={`material-symbols-rounded${className ? ` ${className}` : ''}`}
      color={iconColor}
      fill={fillColor}
      {...spanProps}
    >
      {content}
    </StyledIconWrapper>
  );
};

const StyledIconWrapper = styled.span<{ color: string }>`
  display: inline-flex;

  svg path {
    color: ${(e) => e.color};
    fill: ${(e) => e.color};
  }
` as React.FC<React.PropsWithChildren<any>>;
